"use client";

import {
  DatabaseIcon,
  HashIcon,
  MessagesSquareIcon,
  RssIcon,
  type LucideIcon,
} from "lucide-react";
import type { MouseEvent } from "react";

import { EntityChip } from "./entity-card";
import type { EntityPopoverState } from "./use-entity-popover";
import type { WorkspaceLink } from "./workspace-link";

/* A workspace thing's colour is the one its @mention wears: channels violet,
 * data sets and their records amber, views teal. */
const CHANNEL_TONE = "var(--color-badge-violet)";
const DATA_TONE = "var(--color-badge-amber)";
const VIEW_TONE = "var(--color-badge-teal)";

/** Each kind's disc: its icon and colour. A view's own type icon replaces its kind's. */
const WORKSPACE_LINK_FACES: Record<WorkspaceLink["kind"], { icon: LucideIcon; tone: string }> = {
  channel: { icon: HashIcon, tone: CHANNEL_TONE },
  view: { icon: DatabaseIcon, tone: VIEW_TONE },
  dataSet: { icon: DatabaseIcon, tone: DATA_TONE },
  record: { icon: DatabaseIcon, tone: DATA_TONE },
  post: { icon: MessagesSquareIcon, tone: CHANNEL_TONE },
  feed: { icon: RssIcon, tone: CHANNEL_TONE },
};

/**
 * A link to one of the workspace's own entities as its chip prints it: the
 * kind's colour disc, the entity's name and, beside it, where it lives.
 * WorkspaceLinkChip renders it with the names it looks up and a details
 * popover; a fixture renders it with names it already has.
 */
export function WorkspaceLinkView(props: {
  href: string;
  kind: WorkspaceLink["kind"];
  title: string;
  detail?: string | null;
  /** A view's own type icon; every other kind shows its kind's. */
  icon?: LucideIcon;
  popover?: EntityPopoverState;
  className?: string;
  onOpen?: (event: MouseEvent<HTMLAnchorElement>) => void;
}) {
  const face = WORKSPACE_LINK_FACES[props.kind];
  const Icon = props.icon ?? face.icon;
  return (
    <EntityChip
      link={{ href: props.href }}
      disc={<Icon aria-hidden className="size-[62%] text-white" />}
      discTone={face.tone}
      title={props.title}
      detail={props.detail}
      popover={props.popover}
      className={props.className}
      onClick={props.onOpen}
      dataWorkspaceLink={props.kind}
    />
  );
}
